import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TrackedRun {
  runId: string;
  /** Full URL scope. Optional only because legacy entries from before
   *  the multi-org migration may not carry it; new entries always do. */
  orgId?: string;
  portfolioId?: string;
  projectId: string;
  agentType: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  progress: number;
  progressMessage: string;
  startedAt?: string;
  completedAt?: string;
}

export interface AgentActivityContextType {
  activeRuns: TrackedRun[];
  recentRuns: TrackedRun[];
  trackRun: (orgId: string, portfolioId: string, projectId: string, runId: string, agentType: string) => void;
  trackRuns: (orgId: string, portfolioId: string, projectId: string, runs: Array<{ runId: string; agentType: string }>) => void;
  totalActive: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const AGENT_LABELS: Record<string, string> = {
  'code-vuln': 'Code Vulnerability Scanner',
  sast: 'SAST Engine',
  'secret-scanner': 'Secret Scanner',
  'crypto-audit': 'Cryptography Audit',
  'quantum-readiness': 'Quantum Readiness',
  'malware-detect': 'Malicious Code Detection',
  dependency: 'Dependency Scanner',
  'license-compliance': 'License Compliance',
  'supply-chain': 'Supply Chain Security',
  'container-security': 'Container Security',
  'iac-scanner': 'Infrastructure as Code',
  'cloud-config': 'Cloud Configuration',
  'config-audit': 'Configuration Audit',
  'network-security': 'Network Security',
  'cicd-gate': 'CI/CD Security Gate',
  'api-security': 'API Security',
  'access-control': 'Access Control Analysis',
  'data-privacy': 'Data Privacy',
  compliance: 'Compliance Mapping',
  'threat-model': 'AI Threat Model',
  'pentest-recon': 'Attack Surface Recon',
  'incident-response': 'Incident Response Readiness',
  'security-score': 'Security Scorecard',
  education: 'Security Education',
};

function agentLabel(type: string): string {
  return AGENT_LABELS[type] ?? type;
}

function requestNotificationPermission() {
  if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

function showBrowserNotification(title: string, body?: string) {
  if (
    typeof Notification !== 'undefined' &&
    Notification.permission === 'granted' &&
    typeof document !== 'undefined' &&
    document.hidden
  ) {
    new Notification(title, { body, icon: '/favicon.ico' });
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AgentActivityContext = createContext<AgentActivityContextType | null>(null);

export function useAgentActivity(): AgentActivityContextType {
  const ctx = useContext(AgentActivityContext);
  if (!ctx) {
    throw new Error('useAgentActivity must be used within AgentActivityProvider');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface RunEntry {
  runId: string;
  orgId?: string;
  portfolioId?: string;
  projectId: string;
  agentType: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  progress: number;
  progressMessage: string;
  startedAt?: string;
  completedAt?: string;
  resolvedAt?: number;
}

export function AgentActivityProvider({ children }: { children: ReactNode }) {
  const [runs, setRuns] = useState<Map<string, RunEntry>>(new Map());
  const prevStatusRef = useRef<Map<string, string>>(new Map());
  const permissionRequested = useRef(false);
  const initialFetchDone = useRef(false);

  // On mount, fetch any active runs from the server so we survive page refresh
  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;

    // Active-runs is now org-scoped; pull lastOrgId from localStorage so
    // the indicator survives page reloads.
    const lastOrgId = (() => { try { return localStorage.getItem('lastOrgId'); } catch { return null; } })();
    if (!lastOrgId) return;
    apiClient
      .get<{ runs: Array<{ runId: string; orgId?: string; portfolioId?: string | null; projectId: string; agentType: string; status: string; progress: number; progressMessage: string; startedAt?: string }> }>(
        `/orgs/${lastOrgId}/active-runs`,
      )
      .then((res) => {
        const activeRuns = res.data?.runs;
        if (!activeRuns || activeRuns.length === 0) return;

        setRuns((prev) => {
          const next = new Map(prev);
          for (const r of activeRuns) {
            if (next.has(r.runId)) continue; // already tracked
            next.set(r.runId, {
              runId: r.runId,
              orgId: r.orgId ?? lastOrgId ?? undefined,
              portfolioId: r.portfolioId ?? undefined,
              projectId: r.projectId as string,
              agentType: r.agentType as string,
              status: r.status as RunEntry['status'],
              progress: r.progress ?? 0,
              progressMessage: r.progressMessage ?? '',
              startedAt: r.startedAt,
            });
            prevStatusRef.current.set(r.runId, r.status as string);
          }
          return next;
        });
      })
      .catch(() => {
        // Silently ignore — user may not be logged in yet
      });
  }, []);

  // Cleanup completed runs after 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setRuns((prev) => {
        const now = Date.now();
        let changed = false;
        const next = new Map(prev);
        for (const [id, entry] of next) {
          if (entry.resolvedAt && now - entry.resolvedAt > 60_000) {
            next.delete(id);
            prevStatusRef.current.delete(id);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 10_000);
    return () => clearInterval(interval);
  }, []);

  // Tracked run IDs that are still active (not yet resolved)
  const activeRunIds = Array.from(runs.values()).filter(
    (r) => r.status === 'queued' || r.status === 'running',
  );

  const hasActiveRuns = activeRunIds.length > 0;

  // Poll each tracked run
  useQuery({
    queryKey: ['agent-activity-poll', Array.from(runs.keys()).sort().join(',')],
    queryFn: async () => {
      const entries = Array.from(runs.values()).filter(
        (r) => r.status === 'queued' || r.status === 'running',
      );
      if (entries.length === 0) return null;

      const results = await Promise.allSettled(
        entries.map(async (entry) => {
          // Skip runs that pre-date the multi-org migration (no
          // orgId/portfolioId on the entry); they'll naturally fall off
          // when the user opens a project page that re-tracks them.
          if (!entry.orgId || !entry.portfolioId) {
            return { runId: entry.runId, run: undefined };
          }
          const res = await apiClient.get<{ run: Record<string, unknown> }>(
            `/orgs/${entry.orgId}/portfolios/${entry.portfolioId}/projects/${entry.projectId}/agents/runs/${entry.runId}`,
          );
          return { runId: entry.runId, run: res.data };
        }),
      );

      setRuns((prev) => {
        const next = new Map(prev);
        let changed = false;

        for (const result of results) {
          if (result.status !== 'fulfilled') continue;
          const { runId, run } = result.value;
          if (!run?.run) continue;

          const remote = run.run as Record<string, unknown>;
          const existing = next.get(runId);
          if (!existing) continue;

          const newStatus = (remote.status as string) || existing.status;
          const prevStatus = prevStatusRef.current.get(runId);
          const isTerminal = newStatus === 'completed' || newStatus === 'failed';
          const wasActive = prevStatus === 'queued' || prevStatus === 'running';

          const updated: RunEntry = {
            ...existing,
            status: newStatus as RunEntry['status'],
            progress: (remote.progress as number) ?? existing.progress,
            progressMessage: (remote.progressMessage as string) ?? existing.progressMessage,
            startedAt: (remote.startedAt as string) ?? existing.startedAt,
            completedAt: (remote.completedAt as string) ?? existing.completedAt,
            resolvedAt: isTerminal ? (existing.resolvedAt ?? Date.now()) : undefined,
          };

          // Detect transitions
          if (isTerminal && wasActive && !existing.resolvedAt) {
            const label = agentLabel(existing.agentType);
            if (newStatus === 'completed') {
              const findings = (remote.findingSummary as Record<string, number> | undefined)?.total;
              toast.success(`${label} completed`, {
                description: findings != null ? `${findings} findings detected` : undefined,
              });
              showBrowserNotification(`${label} completed`, findings != null ? `${findings} findings detected` : 'Scan finished');
            } else {
              const errorMsg = (remote.errorMessage as string) || undefined;
              toast.error(`${label} failed`, {
                description: errorMsg,
              });
              showBrowserNotification(`${label} failed`, errorMsg || 'Agent run failed');
            }
          }

          prevStatusRef.current.set(runId, newStatus);
          next.set(runId, updated);
          changed = true;
        }

        return changed ? next : prev;
      });

      return null;
    },
    refetchInterval: 5000,
    enabled: hasActiveRuns,
  });

  const trackRun = useCallback((orgId: string, portfolioId: string, projectId: string, runId: string, agentType: string) => {
    if (!permissionRequested.current) {
      permissionRequested.current = true;
      requestNotificationPermission();
    }
    setRuns((prev) => {
      const next = new Map(prev);
      next.set(runId, {
        runId,
        orgId,
        portfolioId,
        projectId,
        agentType,
        status: 'queued',
        progress: 0,
        progressMessage: 'Queued for execution...',
      });
      return next;
    });
    prevStatusRef.current.set(runId, 'queued');
  }, []);

  const trackRuns = useCallback(
    (orgId: string, portfolioId: string, projectId: string, newRuns: Array<{ runId: string; agentType: string }>) => {
      if (!permissionRequested.current) {
        permissionRequested.current = true;
        requestNotificationPermission();
      }
      setRuns((prev) => {
        const next = new Map(prev);
        for (const r of newRuns) {
          next.set(r.runId, {
            runId: r.runId,
            orgId,
            portfolioId,
            projectId,
            agentType: r.agentType,
            status: 'queued',
            progress: 0,
            progressMessage: 'Queued for execution...',
          });
          prevStatusRef.current.set(r.runId, 'queued');
        }
        return next;
      });
    },
    [],
  );

  const allRuns = Array.from(runs.values());
  const active = allRuns.filter((r) => r.status === 'queued' || r.status === 'running');
  const recent = allRuns.filter(
    (r) => (r.status === 'completed' || r.status === 'failed') && r.resolvedAt != null,
  );

  const value: AgentActivityContextType = {
    activeRuns: active,
    recentRuns: recent,
    trackRun,
    trackRuns,
    totalActive: active.length,
  };

  return (
    <AgentActivityContext.Provider value={value}>
      {children}
    </AgentActivityContext.Provider>
  );
}
